import ServiceOverlay from "@churchofjesuschrist/maps-google-service-overlay";
import PolygonsLayer from "@churchofjesuschrist/maps-google-polygons-layer";
import {getOverlayBounds, polygonsMask} from "@churchofjesuschrist/maps-google-overlays";
import MaskOverlay from "@churchofjesuschrist/maps-google-mask";

export default function AreaBoundaryController($scope, $state, $q, $timeout, google, utils, DataService, OrgService, MapService, MapDrawService, NotificationService, TitleService, MosFrameAnalyticsService) {
	"ngInject";

	const MODIFY_BOUNDARY_ADOBE_ANALYTICS_FORM_NAME = 'Maps - Modify Boundary Form';
	const vm = this;

	vm.$onInit = () => {
		vm.areaId = $state.params.areaId;
		vm.loading = true;
		vm.opts = {
			showOverlaps: false,
			areaSelect: false
		};
		vm.history = [];
		vm.overlays = [];

		DataService.setMapOpts({labelsClean: true});

		DataService.getArea(vm.areaId).then((area) => {
			TitleService.setTitleTranslate("pros-maps.area.boundary.title", area);

			vm.area = area;
			vm.map = DataService.instantMap();
			vm.mode = "select";
			vm.selectable = [];

			MapDrawService.getSelectablePolygons(vm.area).then((results) => {
				vm.overlappingAreas = results.overlappingAreas;
				vm.fixedBoundaries = results.fixedBoundaries;
				vm.border = results.border;
				vm.overlapBorder = results.overlapBorder;

				vm.bounds = getOverlayBounds(vm.border);
				vm.border.setOptions(angular.extend({map: vm.map}, MapDrawService.styles.POLYGON_HIGHLIGHT));
				vm.overlays.push(vm.border);
				vm.recenter();

				vm.mask = polygonsMask(vm.border, {map: vm.map});
				vm.overlays.push(vm.mask);

				vm.overlays.push(new ServiceOverlay({
					ids: results.involvedOrgs.map(MapService.getIdentity),
					map: vm.map
				}));

				results.selectable.forEach(vm.initSelectablePolygon);

				vm.layer = new PolygonsLayer({border: vm.border, polygons: vm.selectable});
				vm.layer.addListener("ready", () => {
					$timeout(() => {
						vm.loading = false;
						if ($scope.$$destroyed) {
							vm.cleanup();
						}
					});
				});
				MosFrameAnalyticsService.formStart(MODIFY_BOUNDARY_ADOBE_ANALYTICS_FORM_NAME);
			}, NotificationService.exception(vm, "pros-maps.error.sync"));
		});

		$scope.$watch(() => (vm.mode), vm.updateMode);

		$scope.$on("$destroy", vm.cleanup);
	};

	vm.cleanup = () => {
		MapDrawService.removeOverlays(vm.overlays);
		vm.removeDrawing();
		if (vm.listeners) {
			vm.listeners.forEach((listener) => {
				google.maps.event.removeListener(listener);
			});
		}
	};

	vm.removeDrawing = () => {
		MapDrawService.removeDrawing(vm.drawing);
		delete vm.drawing;
		vm.setMasked(false);
	};

	vm.recenter = () => (
		MapService.centerMap(vm, vm.map)
	);

	vm.updateMode = () => {
		if (vm.mode && vm.map) {
			let drawMode = vm.mode === "draw";

			vm.removeDrawing();

			vm.setMasked(drawMode);
			if (drawMode) {
				vm.drawing = MapDrawService.createDrawing(vm.map);
			}
		}
	};

	vm.setMasked = (enable) => {
		if (enable && !vm.masked) {
			vm.masked = new MaskOverlay({ map: vm.map });
			vm.listeners = [
				vm.masked.addListener("click", vm.onMaskClick),
				vm.masked.addListener("mousemove", vm.onMaskMouseMove)
			];
		} else {
			vm.masked?.setMap(enable ? vm.map : null);
		}
	};

	vm.initSelectablePolygon = (polygon) => {
		polygon.addListener("click", () => {
 			vm.polygonClicked(polygon);
		});
		vm.selectable.push(polygon);
		vm.overlays.push(polygon);
		polygon.setMap(vm.map);

		vm.refreshOverlaps();
	};

	vm.addSelectablePolygon = (polygon) => (
		MapDrawService.createSelectablePolygon(polygon, vm.overlappingAreas).then((polygon) => {
			vm.initSelectablePolygon(polygon);
			return polygon;
		})
	);

	vm.togglePolygon = (polygon) => {
		let select = !MapDrawService.isPolygonSelected(polygon);
		vm.selectPolygon(polygon, select);
	};

	vm.reselectPolygon = (polygon) => {
		let select = MapDrawService.isPolygonSelected(polygon);
		vm.selectPolygon(polygon, select);
	};

	vm.selectPolygon = (polygon, select) => {
		MapDrawService.selectPolygon(polygon, select);
		vm.refreshOverlaps();
	};

	vm.polygonClicked = (polygon) => {
		switch (vm.mode) {
			case "select":
				vm.togglePolygon(polygon);
				vm.addHistoryEntry(MapDrawService.historyEvent.TOGGLE, polygon);
				break;
		}
	};

	vm.onMaskClick = (e) => {
		if (vm.drawing && !vm.drawing.adding && !vm.drawing.complete && !vm.loading) {
			let position = e.latLng,
				snap = MapDrawService.getSnapPosition(position, vm.drawing, vm.layer, vm.map),
				snapped = snap !== null;
			position = snap || position;
			MapDrawService.addLineSegment(position, snapped, vm.drawing, vm.layer, vm.map).then((lineSegment) => {
				if (!lineSegment.error) {
					if (vm.drawing.complete) {
						let state = MapDrawService.getCurrentState(vm.selectable, vm.drawing);
						vm.addNewSelectablePolygon().then(() => {
							vm.addHistoryEntry(MapDrawService.historyEvent.POLYGON, state);
						});
					} else {
						vm.addHistoryEntry(MapDrawService.historyEvent.VERTEX, lineSegment);
					}
				} else {
					let message = "pros-maps.error.";
					switch (lineSegment.error) {
						case "outside_border":
							message += "outside";
							break;
						case "crosses_itself":
							message += "cross";
							break;
					}
					NotificationService.warning(message);
				}
			});
		}
	};

	vm.onMaskMouseMove = (e) => {
		let position = e.latLng;
		MapDrawService.refreshMouseTrail(position, vm.drawing, vm.layer, vm.map);
	};

	vm.addNewSelectablePolygon = () => {
		vm.loading = true;

		let polygon = new google.maps.Polygon({path: vm.drawing.line.getPath().getArray().slice(1)});
		return MapDrawService.adjustPolygonsLayer(vm.selectable, polygon, vm.fixedBoundaries).then((results) => {
			vm.removeDrawing();

			MapDrawService.removeOverlays(results.replaced);
			results.replaced.forEach((polygon) => {
				utils.removeItem(polygon, vm.selectable);
			});

			let selectCreated = false;
			results.adjustments.forEach((polygon) => {
				let previousMetadata = polygon.get("data");
				vm.addSelectablePolygon(polygon);
				if (previousMetadata) {
					if (previousMetadata.selected) {
						vm.togglePolygon(polygon);
					} else if (previousMetadata.selected === false) {
						selectCreated = true;
					}
				}
			});
			if (selectCreated) {
				results.created.forEach(vm.togglePolygon);
			}

			vm.layer.setExclusions(null);
			vm.layer.setPolygons(vm.selectable);
			vm.updateMode();

			vm.refreshOverlaps();
			vm.loading = false;

			return results;
		}, NotificationService.exception(vm));
	};

	vm.refreshOverlaps = utils.queue(() => {
		MapDrawService.removeOverlays(vm.overlaps);
		vm.overlaps = null;

		if (vm.opts.showOverlaps && (vm.mode === "select" || vm.mode === "draw")) {
			vm.overlaps = MapDrawService.createOverlappingPolygons(vm.selectable, vm.map);
			vm.overlays.push(...vm.overlaps);
		}
		return vm.overlaps;
	});

	vm.compileCurrentSegments = () => (
		MapDrawService.compileSegments(vm.area, vm.selectable, vm.overlappingAreas, vm.overlapBorder)
	);

	vm.compileReassignedSegments = () => {
		let segments = vm.compileCurrentSegments();

		// update segments with reassignments
		vm.unassigned.forEach((unassignedSegment) => {
			let segment = segments[unassignedSegment.index];
			if (!segment.areas.length) {
				segment.areas.push(unassignedSegment.reassign);
			}
		});

		return segments;
	};

	vm.getOtherAreasReassignments = () => {
		let otherAreasPolygons = {},
			segments = vm.compileCurrentSegments();

		vm.unassigned.forEach((unassignedSegment) => {
			let otherAreaId = unassignedSegment.reassign,
				segment = segments[unassignedSegment.index];
			if (otherAreaId !== vm.areaId && !segment.areas.length) {
				// update segment with reassignments
				segment.areas.push(otherAreaId);

				let otherAreaPolygons = otherAreasPolygons[otherAreaId];
				if (!otherAreaPolygons) {
					otherAreasPolygons[otherAreaId] = otherAreaPolygons = [];
				}
				otherAreaPolygons.push(segment.polygon);
			}
		});

		return otherAreasPolygons;
	};

	vm.updateAreaReassignments = (areaId, reassignedPolygons) => (
		DataService.getArea(areaId).then((area) => (
			MapDrawService.getSelectablePolygons(area, reassignedPolygons).then((results) => {
				results.selectable.forEach((polygon) => {
					let metadata = polygon.get("data");
					if (!metadata.selected) {
						for (let i = 0; i < reassignedPolygons.length; i++) {
							let reassignedPolygon = reassignedPolygons[i];
							if (google.maps.geometry.poly.containsLocation(metadata.centroid, reassignedPolygon)) {
								MapDrawService.selectPolygon(polygon, true);
								break;
							}
						}
					}
				});

				let segments = MapDrawService.compileSegments(area, results.selectable);
				return vm.updateAreaBoundary(area, segments);
			})
		))
	);

	vm.updateOtherAreasReassignments = () => {
		//if reassignment to other area, update that area as well
		//get segments for other area reassignments, calc those areas segments, insert new segment (may need to be split in), save
		let otherAreasReassignments = vm.getOtherAreasReassignments(),
			promises = [];
		for (let otherAreaId in otherAreasReassignments) {
			let reassignedPolygons = otherAreasReassignments[otherAreaId];
			promises.push(vm.updateAreaReassignments(parseInt(otherAreaId, 10), reassignedPolygons));
		}

		return $q.all(promises);
	};

	vm.updateAreaBoundary = (area, segments) => (
		MapDrawService.compileUnitBoundaries(area, segments).then((units) => (
			OrgService.updateAreaBoundary(area.id, units).then((area) => (
				// update org data
				DataService.updateArea(area)
			))
		))
	);

	vm.getAvailableAreas = (unitIds) => {
		let availableAreas = [],
			availableAreasIds = [];
		availableAreas.push(vm.area);
		availableAreasIds.push(vm.areaId);
		unitIds.forEach((unitId) => {
			vm.overlappingAreas.forEach((area) => {
				let areaId = area.id;
				area.assigned.forEach((assignedUnitId) => {
					if (assignedUnitId === unitId && availableAreasIds.indexOf(areaId) === -1) {
						availableAreasIds.push(areaId);
						availableAreas.push(area);
					}
				});
			});
		});
		availableAreas.sort((a, b) => {
			let compare = a.id === vm.areaId ? 1 : (b.id === vm.areaId ? -1 : 0);
			if (compare === 0) {
				compare = utils.sortOrgs(a, b);
			}
			return compare;
		});
		return availableAreas;
	};

	vm.loadSegment = (segment) => {
		vm.loading = true;

		if (vm.current) {
			vm.updateSegmentAssignment(vm.current);
			vm.current = null;
			vm.opts.areaSelect = false;
			vm.showReassignedArea();
		}

		$timeout(() => {
			if (segment) {
				vm.current = segment;
				vm.current.polygon.setOptions(MapDrawService.styles.POLYGON_ALERT);
				vm.opts.areaSelect = !!vm.current.reassign || vm.current.available.length === 1;
				if (!vm.current.reassign) {
					vm.current.reassign = vm.current.available[0].id;
				}
			}
			vm.showReassignedArea();

			vm.loading = false;
		});
	};

	vm.showReassignedArea = () => {
		let areaId = vm.current ? vm.current.reassign : null;

		// draw current selection
		let otherSegmentsPolygons = vm.selectable.filter((polygon) => (!vm.current || polygon !== vm.current.polygon)),
			polygons = [];
		if (areaId && areaId !== vm.areaId && vm.opts.areaSelect) {
			otherSegmentsPolygons.forEach((polygon) => {
				if (polygon !== vm.current.polygon) {
					polygon.setOptions(MapDrawService.styles.POLYGON_UNSELECT);
				}
			});

			let area = vm.overlappingAreas.filter((area) => (area.id === areaId))[0];
			DataService.updateMapOpts({staticOrgs: [area]});

			polygons.push(area.boundary);
		} else {
			DataService.updateMapOpts({staticOrgs: null});

			otherSegmentsPolygons.forEach(vm.reselectPolygon);

			otherSegmentsPolygons.filter(MapDrawService.isPolygonSelected).forEach((polygon) => {
				polygons.push(polygon);
			});
		}

		// zoom to selected area
		if (vm.current) {
			let bounds = getOverlayBounds(vm.current.polygon);
			if (vm.opts.areaSelect && vm.current.available.length > 1) {
				polygons.forEach((polygon) => {
					bounds.union(getOverlayBounds(polygon));
				});
			}
			vm.map.fitBounds(bounds);
		} else {
			vm.recenter();
		}
	};

	vm.updateSegmentAssignment = (segment) => {
		if (segment) {
			let selected = segment.reassign === vm.areaId;
			vm.selectPolygon(segment.polygon, selected);
		}
	};

	vm.addHistoryEntry = (type, data) => {
		let entry = MapDrawService.createHistoryEntry(type, data, vm.selectable);
		vm.history.push(entry);
	};

	vm.undo = () => {
		let entry = vm.history.pop();
		switch (entry.type) {
			case MapDrawService.historyEvent.TOGGLE:
				let polygon = vm.selectable[entry.index];
				vm.togglePolygon(polygon);
				break;
			case MapDrawService.historyEvent.VERTEX:
				MapDrawService.removeLastLineSegment(vm.drawing, vm.layer, vm.map);
				break;
			case MapDrawService.historyEvent.POLYGON:
				let state = entry.state;

				MapDrawService.removeOverlays(vm.selectable);
				vm.selectable = state.selectable.map((item) => {
					let polygon = item.polygon;
					vm.selectPolygon(polygon, item.selected);
					polygon.setMap(vm.map);
					return polygon;
				});
				vm.layer.setExclusions(null);
				vm.layer.setPolygons(vm.selectable);

				vm.drawing = MapDrawService.restoreDrawing(state.drawing.path, state.drawing.vertices, vm.map);
				MapDrawService.removeLastLineSegment(vm.drawing, vm.layer, vm.map);

				vm.refreshOverlaps();
				break;
		}
	};

	vm.done = () => {
		vm.loading = true;

		vm.mode = "validate";
		vm.refreshOverlaps();

		vm.missingUnits = [];
		vm.missingChildUnits = [];
		vm.unassigned = [];


		let segments = vm.compileCurrentSegments(),
			assignedUnits = [];
		segments.forEach((segment) => {
			if (MapDrawService.isPolygonSelected(segment.polygon)) {
				assignedUnits.push(...segment.units);
			}
		});

		// 1. check for assigned units not being covered by this area
		vm.missingUnits = DataService.getAssignedBoundaryOrgs(vm.area)
			.filter((unit) => (assignedUnits.indexOf(unit.id) === -1));

		// 2. check for assigned stakes' child wards not being covered by this area
		if (!vm.missingUnits.length) {
			DataService.getAssignedBoundaryOrgs(vm.area).forEach((unit) => {
				if (unit.assigned) {
					let childUnits = DataService.getChildLayerUnits(unit),
						missingChildUnits = childUnits.filter((childUnit) => (assignedUnits.indexOf(childUnit.id) === -1));
					vm.missingChildUnits.push(...missingChildUnits);
				}
			});
		}

		// 3. check for geographical segments not being covered by any area
		if (!vm.missingUnits.length && !vm.missingChildUnits.length) {
			vm.unassigned = segments.filter((segment) => (!segment.areas.length && !segment.outsideOverlapBorder));
			vm.unassigned.forEach((segment) => {
				segment.available = vm.getAvailableAreas(segment.units);
			});
			vm.unassigned.sort((a, b) => (
				b.available.length - a.available.length
			));

			vm.loadSegment(vm.unassigned[0]);
		} else {
			DataService.updateMapOpts({staticOrgs: vm.missingUnits.length ? vm.missingUnits : vm.missingChildUnits});
			vm.loading = false;
		}
	};

	vm.back = () => {
		let index = vm.current ? vm.unassigned.indexOf(vm.current) : vm.unassigned.length,
			previousIndex = index - 1;
		vm.loadSegment(vm.unassigned[previousIndex]);
		if (previousIndex < 0) {
			vm.mode = "select";
			vm.refreshOverlaps();
			DataService.updateMapOpts({staticOrgs: null});
		}
	};

	vm.next = () => {
		let index = vm.unassigned.indexOf(vm.current),
			nextIndex = index + 1;
		vm.loadSegment(vm.unassigned[nextIndex]);
	};

	vm.save = () => {
		vm.loading = true;

		let segments = vm.compileReassignedSegments();
		vm.updateAreaBoundary(vm.area, segments).then((area) => (
			vm.updateOtherAreasReassignments().then(() => {
				NotificationService.success("pros-maps.success.saved.area", null, area);
				MosFrameAnalyticsService.formSubmit(MODIFY_BOUNDARY_ADOBE_ANALYTICS_FORM_NAME);
				vm.exit();
			})
		), () => {
			NotificationService.exception(vm);
			MosFrameAnalyticsService.formError(MODIFY_BOUNDARY_ADOBE_ANALYTICS_FORM_NAME, ['server-error']);
		});
	};

	vm.exit = () => {
		$state.go("area", {areaId: vm.areaId});
	};

}
